/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// Medaica Report document — the report shell at visual fidelity with the
// prototype page (cardiac_report_v0_5_6_dev_2.html, header as of v0.6.1):
// indigo header band (brand row + lifecycle stepper + coded-patient identity
// strip), the controls row inside the card, then the patient container
// (result cards → follow-up) or the provider prose document (questionnaire →
// notes → document), and the disclaimer footer. The patient questionnaire is
// reviewer context: it renders in the provider view and while editing, never
// in the patient's own report. The Help control lives in the controls row
// the host supplies, beside the other page actions.
//
// The brand row is the Medaica logo and a "Report" button that opens the
// brand menu: Quick Start (the host's help dialog), Download diagnostic log
// (the session's interaction log, report-action-log.ts) and About. The
// prototype's menu also carries Edit Report and Download Report; here Edit
// sits in the controls row and the HTML download is not ported. Outside
// click and Escape close the menu; choosing an item or pressing Escape
// returns focus to the button (the prototype has no focus handling).
//
// Edit mode keeps the same shell (band, stepper, controls, Signal Quality,
// questionnaire, follow-up) and swaps the result cards for the admin body
// the page supplies as children — the prototype's admin mode re-renders
// inside the same page rather than replacing it.
//
// Patient-surface rules from the handoff's third-party evidence spec
// (`pcg_exam_viewer_third_party_evidence_spec.md`) §16, enforced here and below:
//   - the lifecycle stepper is hidden in the patient view
//   - no confidence numbers, no provenance, no edit/audit traces

import React, { ReactNode, useEffect, useId, useRef, useState } from "react"
import HeartReportFollowUp, { ReportLike } from "./HeartReportFollowUp"
import HeartReportCards from "./HeartReportCards"
import HeartReportProviderDocument, { ProviderReportLike } from "./HeartReportProviderDocument"
import HeartReportQuestionnaire from "./HeartReportQuestionnaire"
import HeartReportAboutDialog from "./HeartReportAboutDialog"
import { downloadReportDiagnosticLog, useReportActionLog } from "./report-action-log"
import MedaicaLogo from "../medaica-logo"
import "./heart-report.css"

export type HeartReportViewMode = "patient" | "provider"

const PANEL_STAGES: { state: string; label: string }[] = [
  { state: "preliminary", label: "Preliminary" },
  { state: "in_review", label: "In Review" },
  { state: "approved", label: "Approved" },
  { state: "released", label: "Released" },
]

/**
 * Lifecycle stepper in the header band (prototype #panelStateStepper +
 * updatePanelStateBadge): stages before the current one are completed (✓)
 * and the current one is active — except that reaching Approved or Released
 * marks that stage completed too, since each is a sign-off rather than a
 * stage still in progress.
 */
export const HeartReportStepper: React.FC<{ panelState: string }> = ({ panelState }) => {
  const currentIndex = PANEL_STAGES.findIndex((stage) => stage.state === panelState)
  return (
    <div className="phr-stepper" data-testid="panel-stepper">
      {PANEL_STAGES.map((stage, index) => {
        const signedOff = panelState === "approved" || panelState === "released"
        const completed = index < currentIndex || (signedOff && index === currentIndex)
        const active = !completed && index === currentIndex
        return (
          <React.Fragment key={stage.state}>
            {index > 0 ? <div className={`state-connector${index <= currentIndex ? " completed" : ""}`} /> : null}
            <div className={`state-step${completed ? " completed" : active ? " active" : ""}`} data-state={stage.state}>
              <div className="state-icon">{completed ? "✓" : index + 1}</div>
              <span className="state-label">{stage.label}</span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

/**
 * The brand row of the header band (prototype #reportBrandRow + #reportBrandMenu):
 * the logo beside a "Report" button that opens the brand menu.
 */
const HeartReportBrandMenu: React.FC<{ showLogo?: boolean; onQuickStart?: () => void }> = ({
  showLogo = false,
  onQuickStart,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!menuOpen) return undefined
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return
      setMenuOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [menuOpen])

  const choose = (action: () => void): void => {
    setMenuOpen(false)
    triggerRef.current?.focus()
    action()
  }

  return (
    <>
      <div className="phr-brand-row" data-testid="heart-report-brand">
        {showLogo ? (
          <MedaicaLogo
            className="phr-logo"
            iColor="currentColor"
            role="img"
            aria-label="Medaica"
            data-testid="heart-report-logo"
          />
        ) : null}
        <button
          ref={triggerRef}
          type="button"
          className="phr-brand-title"
          title="Report menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          onClick={() => setMenuOpen((open) => !open)}
        >
          Report
        </button>
      </div>
      {menuOpen ? (
        <div ref={menuRef} id={menuId} className="phr-brand-menu phr-screen-only" role="menu" aria-label="Report menu">
          <button type="button" role="menuitem" onClick={() => choose(() => onQuickStart?.())}>
            Quick Start
          </button>
          <button type="button" role="menuitem" onClick={() => choose(downloadReportDiagnosticLog)}>
            Download diagnostic log
          </button>
          <button type="button" role="menuitem" onClick={() => choose(() => setAboutOpen(true))}>
            About
          </button>
        </div>
      ) : null}
      <HeartReportAboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  )
}

export interface HeartReportProps {
  report: ReportLike & ProviderReportLike
  viewMode?: HeartReportViewMode
  /** Formatted exam date for the identity strip (export creation time). */
  examDate?: string
  /** Immutable export version the report was derived from (footer provenance). */
  exportVersion?: number | string
  /**
   * Shows the Medaica logo beside the Report button (the prototype prepends
   * its logo into the brand row at init). Off by default, so a host that
   * shows its own branding gets none.
   */
  showLogo?: boolean
  /** Opens the host's Quick Start (help) dialog, from the brand menu. */
  onQuickStart?: () => void
  /**
   * Platform demographics: fills the identity
   * strip and the provider document's demographics block. Omitted fields
   * keep the prototype's placeholder rendering ("Coded patient" / em-dash).
   * The host app applies its own access rules (e.g. cross-org name masking)
   * before passing values here.
   */
  demographics?: {
    patientName?: string
    dob?: string
    age?: string
    sex?: string
    patientId?: string
    examDate?: string
  }
  /**
   * Report lifecycle state for the header stepper. Rendered outside the
   * patient view only (prototype rule: hidden while the patient report is
   * showing, visible in the provider view and in edit mode).
   */
  panelState?: string
  /**
   * Controls row rendered inside the card under the header band — the
   * prototype's view-toggle row (Patient/Provider pills on the left,
   * Edit and the icon actions on the right). Screen-only.
   */
  controls?: ReactNode
  /**
   * Edit mode: the admin body passed as children replaces the result cards
   * while the rest of the shell stays in place.
   */
  editMode?: boolean
  /** Signal Quality (Good / Fair / Poor) shown above the cards in edit mode only. */
  signalQuality?: string | null
  /**
   * The reviewer-observation notes, rendered after the questionnaire in
   * every mode (prototype container order): the Report notes in view mode
   * and print, plus the internal Viewer observations while editing.
   */
  notesSection?: ReactNode
  /**
   * A notice about the state of the review (cards still waiting for a
   * decision), rendered under the controls row. Screen-only.
   */
  banner?: ReactNode
  /**
   * The larger interface size for clinical review. Applies in edit mode
   * only; the report views and print are never enlarged.
   */
  reviewSizeLarge?: boolean
  children?: ReactNode
}

// Prototype signalQualityStyle(): Good reads green, Poor red, anything else amber.
const signalQualityTone = (quality: string): "good" | "poor" | "fair" => {
  if (quality === "Good") return "good"
  if (quality === "Poor") return "poor"
  return "fair"
}

const IdentityField: React.FC<{ label: string; value: string; wide?: boolean }> = ({ label, value, wide }) => (
  <div className="phr-identity-field">
    <label>{label}</label>
    <span className={`phr-identity-value${wide ? " phr-wide" : ""}`}>{value}</span>
  </div>
)

export const HeartReport: React.FC<HeartReportProps> = ({
  report,
  viewMode = "patient",
  examDate,
  exportVersion,
  showLogo = false,
  onQuickStart,
  demographics = {},
  panelState,
  controls,
  editMode = false,
  signalQuality,
  notesSection,
  banner,
  reviewSizeLarge = false,
  children,
}) => {
  // Prototype renderFeatures(): the provider prose document renders only
  // for the provider view outside admin mode; everything else (patient
  // view, any edit session) uses the patient container layout.
  const patientContainer = editMode || viewMode !== "provider"
  // The stepper shows in both report views: the patient view hid it at
  // prototype parity, but knowing where the report stands matters in either
  // view now that the lifecycle is persisted.
  const showStepper = Boolean(panelState)
  const showSignalQuality = editMode && Boolean(signalQuality)
  useReportActionLog()
  return (
    <div
      className={`pcg-heart-report${editMode && reviewSizeLarge ? " phr-review-size-large" : ""}`}
      data-testid="heart-report"
      data-view-mode={viewMode}
      data-edit-mode={editMode ? "true" : undefined}
    >
      <div className="phr-container">
        <header className="phr-header">
          <div className="phr-brand">
            <HeartReportBrandMenu showLogo={showLogo} onQuickStart={onQuickStart} />
            {showStepper ? <HeartReportStepper panelState={panelState as string} /> : null}
          </div>
          <div className="phr-identity" data-testid="heart-report-identity">
            {/* Platform demographics when the host supplies them; otherwise the
                prototype's coded-patient defaults and em-dash placeholders. */}
            <IdentityField label="Review Subject" value={demographics.patientName || "Coded patient"} wide />
            <IdentityField label="DOB" value={demographics.dob || "—"} />
            <IdentityField label="Exam Date" value={demographics.examDate || examDate || "—"} />
            <IdentityField label="Patient ID" value={demographics.patientId || "—"} wide />
            <IdentityField label="Age" value={demographics.age || "—"} />
            <IdentityField label="Sex" value={demographics.sex || "—"} />
          </div>
        </header>
        <div className="phr-body">
          {controls ? (
            <div className="phr-controls phr-screen-only" data-testid="heart-report-controls">
              {controls}
            </div>
          ) : null}
          {banner ? <div className="phr-screen-only">{banner}</div> : null}
          {patientContainer ? (
            <>
              {/* Prototype renderRecordingQualityNote(): the Signal Quality
                  status pill outside the patient report mode */}
              {showSignalQuality ? (
                <div className="phr-quality-row phr-screen-only">
                  <div className="phr-signal-quality" data-testid="signal-quality">
                    <span className="phr-signal-quality-label">Signal Quality</span>
                    <span
                      className={`phr-signal-quality-value phr-signal-quality-value--${signalQualityTone(
                        signalQuality as string
                      )}`}
                    >
                      <span className="phr-signal-quality-dot" aria-hidden="true" />
                      {signalQuality}
                    </span>
                  </div>
                </div>
              ) : null}
              {/* The patient supplied these answers; they are clinical context
                  for the reviewer and the provider report, not content repeated
                  in the patient's own report. Shown here only while editing
                  (container order: questionnaire above the cards) */}
              {editMode ? (
                <HeartReportQuestionnaire questionnaire={(report as { questionnaire?: unknown }).questionnaire} />
              ) : null}
              {notesSection}
              {editMode ? (
                <div data-testid="heart-report-edit">{children}</div>
              ) : (
                <HeartReportCards features={report.features as never} />
              )}
              <HeartReportFollowUp report={report} />
            </>
          ) : (
            <>
              <HeartReportQuestionnaire questionnaire={(report as { questionnaire?: unknown }).questionnaire} />
              {notesSection}
              <HeartReportProviderDocument report={report} demographics={{ examDate, ...demographics }} />
            </>
          )}
        </div>
        <footer className="phr-footer" data-testid="heart-report-footer">
          Medaica Report
          {exportVersion !== undefined && exportVersion !== null ? ` · Export v${exportVersion}` : ""} · Provisional
          results are review aids, not diagnostic. Not an ECG or echocardiogram.
        </footer>
      </div>
    </div>
  )
}

export default HeartReport
